// Module 18 device-side contracts (ADR-025, adapters & edge stream): edge agents and their signed
// commands, peripherals, device jobs (print / encode / identity read), card encode holds, ANPR
// sources, update manifests, adapter contract runs and the connector catalogue. Browser-safe.
import { z } from 'zod';

const Uuid = z.uuid();
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const Version = z.number().int().min(1);
const Note = z.string().trim().min(3, 'v.reasonShort').max(500, 'v.tooLong');
const Iso = z.iso.datetime({ offset: true });
const SemVer = z.string().regex(/^[0-9]{1,4}\.[0-9]{1,4}\.[0-9]{1,6}([-+][A-Za-z0-9.-]{1,40})?$/, 'v.invalid');
const PublicKey = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'v.invalid');
const Nonce = z.string().regex(/^[A-Za-z0-9_-]{16,64}$/, 'v.invalid');
const LocalRef = z.string().regex(/^[a-z0-9][a-z0-9._-]{0,39}$/, 'v.invalid');

export const VALIDATION_STATUS_VALUES = ['simulator', 'standards-based-untested-with-vendor', 'unavailable'] as const;
export type ValidationStatusValue = (typeof VALIDATION_STATUS_VALUES)[number];
export const PERIPHERAL_KIND_VALUES = ['printer', 'encoder', 'id_reader', 'mrz_reader', 'anpr_camera', 'osdp_reader'] as const;

// ------------------------------------------------------------------ agent → server (device-authenticated)
export const PeripheralReport = z.object({
  localRef: LocalRef,
  kind: z.enum(PERIPHERAL_KIND_VALUES),
  driver: z.string().regex(/^[a-z][a-z0-9._-]{1,59}$/, 'v.invalid'),
  model: z.string().trim().max(80).nullable().optional(),
  firmware: z.string().trim().max(40).nullable().optional(),
  validation: z.enum(VALIDATION_STATUS_VALUES),
});
export type PeripheralReport = z.infer<typeof PeripheralReport>;

export const EdgeEnrolRequest = z.object({ publicKey: PublicKey, agentVersion: SemVer, platform: z.string().trim().max(60).nullable().optional(), peripherals: z.array(PeripheralReport).max(32).default([]) });
export const EdgeStateRequest = z.object({
  agentVersion: SemVer,
  bufferDepth: z.number().int().min(0).max(10_000_000),
  bufferDropped: z.number().int().min(0).max(10_000_000),
  peripherals: z.array(PeripheralReport).max(32).default([]),
  update: z.object({ checkedVersion: SemVer.nullable(), available: z.boolean(), verified: z.boolean() }).optional(),
});
export const EdgeAckRequest = z.object({ nonce: Nonce });
export const EdgeResultRequest = z.object({
  nonce: Nonce,
  outcome: z.enum(['succeeded', 'failed', 'unknown', 'rejected']),
  detail: z.string().trim().max(300).nullable(),
  executedAt: Iso,
  data: z.record(z.string(), z.unknown()).default({}),
});
export const EdgeEventsRequest = z.object({
  events: z
    .array(
      z.object({
        eventId: Uuid,
        seq: z.number().int().min(0),
        kind: z.enum(['anpr.read', 'peripheral.state', 'buffer.gap']),
        peripheral: LocalRef.nullable(),
        occurredAt: Iso,
        payload: z.record(z.string(), z.unknown()),
      }),
    )
    .min(1)
    .max(100),
  bufferDepth: z.number().int().min(0).max(10_000_000).default(0),
  bufferDropped: z.number().int().min(0).max(10_000_000).default(0),
});
export const EdgeKeyRotateRequest = z.object({ publicKey: PublicKey });

// ------------------------------------------------------------------ staff → server
export const DecidePeripheralRequest = z.object({ expectedVersion: Version, decision: z.enum(['allowed', 'blocked']) });
export const DiagnosticCommandRequest = z.object({ kind: z.enum(['diagnostic.ping', 'printer.test']), peripheralId: Uuid.optional() });
export const EdgePrintRequest = z.object({ printJobId: Uuid, peripheralId: Uuid });
export const EncodeCardRequest = z.object({ cardId: Uuid, peripheralId: Uuid });
export const EncodeReadBackRequest = z.object({ peripheralId: Uuid });
export const ReleaseCardHoldRequest = z.object({ note: Note });
export const EdgeReaderCaptureRequest = z.object({ participationId: Uuid, peripheralId: Uuid, replaceCurrent: z.boolean().default(false) });
export const PublishUpdateManifestRequest = z.object({ channel: z.enum(['stable', 'pilot']), agentVersion: SemVer, artifactSha256: z.string().regex(/^[0-9a-f]{64}$/, 'v.invalid'), artifactSize: z.number().int().min(1).max(2_000_000_000), notes: z.string().trim().max(500).nullable().optional() });
export const ContractRunRequest = z.object({ adapterCode: z.string().regex(/^[a-z][a-z0-9_.-]{1,59}$/, 'v.invalid') });
export const ValidateAdapterConfigRequest = z.object({ config: z.record(z.string(), z.unknown()).default({}), secretRefs: z.record(z.string(), z.string().max(200)).default({}) });

const Lane = z.object({ gateLabel: z.string().trim().max(80).optional(), direction: z.enum(['entry', 'exit', 'unknown']).optional() });
const Jurisdiction = z.string().regex(/^([A-Z]{2}(-[A-Z0-9]{1,3})?|XX)$/, 'v.invalid');
export const CreateAnprSourceRequest = z
  .object({
    siteId: Uuid,
    name: trimmed(80),
    transport: z.enum(['http_push', 'edge_relay']),
    sourceRef: z.string().regex(/^[a-z0-9][a-z0-9._-]{0,59}$/, 'v.invalid'),
    agentId: Uuid.optional(),
    lanes: z.record(z.string().max(60), Lane).default({}),
    defaultJurisdiction: Jurisdiction.nullable().optional(),
    confidenceScale: z.enum(['unit', 'percent']).default('unit'),
    skewToleranceSeconds: z.number().int().min(5, 'v.range').max(3600, 'v.range').default(120),
    lateAfterSeconds: z.number().int().min(10, 'v.range').max(86_400, 'v.range').default(300),
  })
  .refine((r) => (r.transport === 'edge_relay') === !!r.agentId, { message: 'adp.v.agentForRelay', path: ['agentId'] });
export const UpdateAnprSourceRequest = z.object({
  expectedVersion: Version,
  name: trimmed(80).optional(),
  status: z.enum(['active', 'disabled']).optional(),
  lanes: z.record(z.string().max(60), Lane).optional(),
  defaultJurisdiction: Jurisdiction.nullable().optional(),
  confidenceScale: z.enum(['unit', 'percent']).optional(),
  skewToleranceSeconds: z.number().int().min(5, 'v.range').max(3600, 'v.range').optional(),
  lateAfterSeconds: z.number().int().min(10, 'v.range').max(86_400, 'v.range').optional(),
});
export const RotateAnprSecretRequest = z.object({ overlapHours: z.number().int().min(0, 'v.range').max(168, 'v.range').default(24) });

// ------------------------------------------------------------------ views
export interface PeripheralView {
  readonly id: string;
  readonly localRef: string;
  readonly kind: string;
  readonly driver: string;
  readonly model: string | null;
  readonly firmware: string | null;
  readonly validation: ValidationStatusValue;
  readonly firmwareValidated: boolean;
  readonly status: 'pending' | 'allowed' | 'blocked';
  readonly lastReportedAt: string;
  readonly version: number;
}

export interface EdgeAgentView {
  readonly deviceId: string;
  readonly siteId: string;
  readonly name: string;
  readonly deviceStatus: string;
  readonly connectivity: string;
  readonly lastSeenAt: string | null;
  readonly keyFingerprint: string;
  readonly keyRotatedAt: string | null;
  readonly agentVersion: string;
  readonly platform: string | null;
  readonly enrolledAt: string;
  readonly buffer: { readonly depth: number; readonly dropped: number };
  readonly update: Readonly<Record<string, unknown>>;
  readonly peripherals: readonly PeripheralView[];
  readonly commands: { readonly pending: number; readonly unknown: number; readonly rejected: number };
}

export interface EdgeCommandView {
  readonly id: string;
  readonly agentId: string;
  readonly peripheralId: string | null;
  readonly kind: string;
  readonly capability: string;
  readonly status: string;
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly acknowledgedAt: string | null;
  readonly settledAt: string | null;
  readonly resultOutcome: string | null;
  readonly resultDetail: string | null;
  readonly lateResult: boolean;
  readonly keyId: string;
}

export interface DeviceJobView {
  readonly id: string;
  readonly siteId: string;
  readonly kind: 'print' | 'encode' | 'encode_read_back' | 'read_identity';
  readonly status: string;
  readonly statusReason: string | null;
  readonly agentId: string;
  readonly peripheralId: string;
  readonly printJobId: string | null;
  readonly cardId: string | null;
  readonly cardNumber: string | null;
  readonly participationId: string | null;
  readonly evidenceId: string | null;
  readonly commandId: string | null;
  readonly result: Readonly<Record<string, unknown>>;
  readonly hold: { readonly id: string; readonly reason: string; readonly openedAt: string } | null;
  readonly createdAt: string;
  readonly settledAt: string | null;
  readonly version: number;
}

export interface CardHoldView {
  readonly id: string;
  readonly cardId: string;
  readonly cardNumber: string;
  readonly custody: string;
  readonly jobId: string;
  readonly reason: string;
  readonly openedAt: string;
  readonly releasedAt: string | null;
  readonly releaseBasis: string | null;
}

export interface AnprSourceView {
  readonly id: string;
  readonly siteId: string;
  readonly name: string;
  readonly transport: 'http_push' | 'edge_relay';
  readonly sourceRef: string;
  readonly agentId: string | null;
  readonly lanes: Readonly<Record<string, { gateLabel?: string; direction?: string }>>;
  readonly defaultJurisdiction: string | null;
  readonly confidenceScale: 'unit' | 'percent';
  readonly skewToleranceSeconds: number;
  readonly lateAfterSeconds: number;
  readonly secretHint: string | null;
  readonly status: 'active' | 'disabled';
  readonly ingestPath: string | null;
  readonly recent: { readonly recorded: number; readonly duplicates: number; readonly rejected: number; readonly skewed: number; readonly late: number; readonly lastReceivedAt: string | null };
  readonly version: number;
}

export interface AnprSourceSecretIssued {
  readonly source: AnprSourceView;
  readonly secret: string;
}

export interface AnprPushResult {
  readonly results: ReadonlyArray<{ readonly eventId: string; readonly outcome: 'recorded' | 'duplicate' | 'rejected'; readonly reason: string | null; readonly readOutcome: string | null; readonly clockSkewed: boolean; readonly late: boolean; readonly exceptionId: string | null }>;
}

export interface UpdateManifestView {
  readonly id: string;
  readonly channel: 'stable' | 'pilot';
  readonly agentVersion: string;
  readonly artifactSha256: string;
  readonly artifactSize: number;
  readonly notes: string | null;
  readonly keyId: string;
  readonly status: 'published' | 'withdrawn';
  readonly publishedAt: string;
}

export interface ContractResultView {
  readonly id: string;
  readonly feature: string;
  readonly acceptance: string;
  readonly passed: boolean;
  readonly detail: string | null;
  readonly durationMs: number;
}

export interface ContractRunView {
  readonly id: string;
  readonly adapterCode: string;
  readonly adapterVersion: string;
  readonly target: 'simulator' | 'local_fake';
  readonly passed: number;
  readonly failed: number;
  readonly results: readonly ContractResultView[];
  readonly runAt: string;
}

export interface CatalogueEntryView {
  readonly code: string;
  readonly kind: string;
  readonly label: string;
  readonly platform: string | null;
  readonly adapterVersion: string | null;
  readonly validation: ValidationStatusValue;
  readonly validatedVersions: readonly string[];
  readonly operations: readonly string[];
  readonly capabilities: Readonly<Record<string, unknown>>;
  readonly limitations: readonly string[];
  readonly runnable: boolean;
  readonly contractTestable: boolean;
  readonly lastRun: { readonly passed: number; readonly failed: number; readonly runAt: string } | null;
}
